<template>
  <div class="product-buy-gallery">
    <div ref="swiperEl" class="product-buy-gallery_main swiper">
      <div class="swiper-wrapper">
        <div
          v-for="(image, index) in images"
          :key="image"
          class="product-buy-gallery_slide swiper-slide"
        >
          <img :src="image" :alt="altOf(index)" />
        </div>
      </div>
    </div>

    <div v-if="images.length > 1" class="product-buy-gallery_thumbs">
      <button
        v-for="(image, index) in images"
        :key="image"
        type="button"
        class="product-buy-gallery_thumb"
        :class="{ 'is-active': index === activeIndex }"
        :aria-label="`${index + 1}枚目の画像を表示`"
        :aria-current="index === activeIndex"
        @click="goTo(index)"
      >
        <img :src="image" alt="" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * 支給デザインの `product-buy-gallery`（商品画像のスライダー＋サムネイル）。38/38 と `/set` に出る。
 *
 * **スライドは2〜6枚の可変**（2枚20商品・3枚5・4枚6・5枚6・6枚1。`/set` は4枚）。
 * 画像は商品別で、既存サイトに相当するものが無い全数新規（支給 `page-<コード>_img00N.png`）。
 *
 * 支給は jQuery ＋ slick（`design-src/20260810/assets/js/script_main.js:212-249`）。
 * jQuery と slick は持ち込まない（決定#11）ので、FVカルーセルと同じ **Swiper** で置き換える。
 * 支給の設定はそのまま移せる（`slidesToShow: 1` / `arrows: false` / `dots: false` /
 * `infinite: true` → `loop` / `speed: 300`）。サムネイルは slick の `asNavFor` ではなく
 * `data-index` を読んで `slickGoTo` する自前実装なので、こちらも `goTo()` で素直に置き換わる。
 *
 * **支給CSS に直下セレクタが無い**（`renewal/style.css:6274-6325` はすべてクラス単独か子孫）ため、
 * Swiper が必要とする `.swiper-wrapper` を `_main` と `_slide` の間に挟んでも崩れない。
 *
 * `alt` を入れるのは1枚目だけにする。2枚目以降は同じ商品の別カットで、支給も `alt=""` にしている。
 * **ただし1枚ずつ中身が違う画面（`/set` の4セット）は `alts` で1枚ずつ渡す。**
 * サムネイルは画像が装飾なので `alt=""` とし、押せる要素としての名前は `aria-label` で与える。
 *
 * **1枚しか無い商品ではサムネイルを出さない**（支給に1枚の商品は無いが、`/set` を含め
 * データ次第で起こりうるため）。
 * → CONVERSION-GUIDE.md §3.3.1 の #4
 */
import type SwiperClass from 'swiper';
import 'swiper/css';

type Props = {
  /** 画像パスの配列。2〜6枚 */
  images: string[];
  /** 1枚目の代替テキスト。商品名を渡す */
  alt: string;
  /**
   * 画像ごとの代替テキスト。**1枚ずつ中身が違うときだけ渡す。**
   * 商品詳細は同じ商品の別カットなので渡さない（`alt` を1枚目にだけ入れる）が、
   * `/set` は4枚が別々のセット商品なので、それぞれのセット名を渡す。
   */
  alts?: string[];
};

const props = defineProps<Props>();

/** `alts` があればそれを、無ければ従来どおり1枚目にだけ `alt` を入れる */
const altOf = (index: number): string => props.alts?.[index] ?? (index === 0 ? props.alt : '');

const swiperEl = ref<HTMLElement | null>(null);
const activeIndex = ref<number>(0);
let swiper: SwiperClass | null = null;

const goTo = (index: number): void => {
  activeIndex.value = index;
  // Swiper 未初期化（SSR 直後やクライアントJSが落ちている場合）はサムネイルの選択状態だけ動かす
  swiper?.slideToLoop(index);
};

// Swiper 本体はクライアントでだけ読む。SSR では素の `swiper-wrapper` が出るので初期表示は落ちない
onMounted(async () => {
  if (!swiperEl.value || props.images.length <= 1) return;

  const [{ default: Swiper }, { A11y, Keyboard }] = await Promise.all([
    import('swiper'),
    import('swiper/modules'),
  ]);

  swiper = new Swiper(swiperEl.value, {
    modules: [A11y, Keyboard],
    slidesPerView: 1,
    loop: true,
    speed: 300,
    // 支給は `arrows: false` / `dots: false`。送りはスワイプとサムネイルだけ
    allowTouchMove: true,
    keyboard: { enabled: true },
    on: {
      slideChange: (instance: SwiperClass) => {
        activeIndex.value = instance.realIndex;
      },
    },
  });
});

onBeforeUnmount(() => {
  swiper?.destroy();
  swiper = null;
});
</script>
